import React, { useCallback, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "index";
import { useTranslation } from "react-i18next";

// Components
import { StatusTableColumns } from "./statusTable/Columns";
import { TableCell } from "@trackman/web-shared-components";
import CheckIcon from "@mui/icons-material/Check";
import StatusTableFilters from "./statusTable/StatusTableFilters";
import { AppCopyText, AppPageHeader, AppUseIcon } from "components/ui";
import { AppSectionErrorBoundary } from "components/ui/AppSectionErrorBoundary";
import { SimpleTable } from "components/Tables/SimpleTable";
import { Sort } from "components/Tables/Sort";

export const StatusPage: React.FC = observer(() => {
  const { statusStore } = useStores();
  const { page, totalItemCount, take, sortOrderBy, sortType } = statusStore;
  const { t } = useTranslation();

  useEffect(() => {
    statusStore.fetchStatus({ pageNumber: 1, take: 10 });
  }, [sortOrderBy, sortType]);

  const rowMapper = (cells: any[]) => {
    return (
      <>
        {cells.map(({ getCellProps, column, row, render, value }) => (
          <TableCell
            {...getCellProps()}
            className="px-3 cursor-pointer"
            align={column.align}
          >
            <div className=" w-100 d-flex font-inherit ">
              {renderCell({
                cellValue: value,
                columnHeader: column.Header,
                columnId: column.id,
              })}
            </div>
          </TableCell>
        ))}
      </>
    );
  };

  const onChangePageHandler = useCallback(async (page) => {
    await statusStore.fetchStatus({ pageNumber: page, take });
  }, []);

  const handleHeaderClickSort = useCallback((columnKey: string) => {
    const key = {
      ptuConnected: "PTUConnected",
      severity: "State",
      heartbeat: "Heartbeat",
      serverProvisioned: "ServerProvisioned",
    };

    if (!key[columnKey]) return;
    statusStore.setSortOrderBy(key[columnKey]);

    return;
  }, []);

  const renderCell = ({
    cellValue,
    columnHeader,
    columnId,
  }: {
    cellValue: string;
    columnHeader: string;
    columnId: string;
  }) => {
    if (columnId === "ptuConnected")
      return (
        <>
          <AppUseIcon
            name={cellValue ? "SuccessCircleIcon" : "DangerCircleIcon"}
            width="16"
            height="16"
          />
        </>
      );

    if (
      columnId === "serverProvisioned" ||
      columnId === "ram" ||
      columnId === "hdd" ||
      columnId === "cpu" ||
      columnId === "uploadingData"
    ) {
      return (
        <>
          {!cellValue ? (
            <CheckIcon className="font-18" data-testid="checked-icon" />
          ) : (
            "-"
          )}
        </>
      );
    }

    if (
      columnId === "ipAddress" ||
      columnId === "ptuSerialnumber" ||
      columnId === "systemId"
    ) {
      return (
        <div className="d-flex justify-content-between align-items-center w-100">
          <p
            data-test={`systemID-cell-text`}
            className="mb-0 overflow-ellipsis"
            title={cellValue}
          >
            {cellValue || "-"}
          </p>
          <AppCopyText className="ml-1 " text={cellValue} />
        </div>
      );
    }

    if (columnId === "heartbeat") {
      return getTimeText(new Date(cellValue));
    }

    if (columnId === "severity") {
      return (
        <>
          {getTitleIcon(cellValue)} {cellValue}
        </>
      );
    }

    // if (columnId === "lastUpdatedAt") {
    //   return formatDate(cellValue) || cellValue;
    // }

    // if (columnId === "type") {
    //   return <AppSystemTypeChip systemType={cellValue as systemTypes} />;
    // }

    return (
      <div className="overflow-ellipsis" title={cellValue || "-"}>
        {cellValue || "-"}
      </div>
    );
  };

  return (
    <AppSectionErrorBoundary>
      <div className="container-fluid" style={{ marginTop: "35px" }}>
        <AppPageHeader title={t("systemStatus")} />

        <SimpleTable
          rowMapper={rowMapper}
          onChangePage={onChangePageHandler}
          columns={StatusTableColumns}
          data={statusStore.status?.data || []}
          page={page}
          rowsPerPage={take}
          filters={StatusTableFilters({ store: statusStore })}
          isLoading={statusStore.isLoading}
          sortedColumn={
            statusStore.sortOrderBy === "State"
              ? "severity"
              : statusStore.sortOrderBy
          }
          sortType={statusStore.sortType ? Sort.asc : Sort.desc}
          onHeaderClick={handleHeaderClickSort}
          count={totalItemCount}
        />
      </div>
    </AppSectionErrorBoundary>
  );
});

const getTimeText = (dateObject: Date) => {
  var dateString: string;
  var timeString: string;

  var date: number = dateObject.getUTCDate();
  var month: number = dateObject.getUTCMonth() + 1;
  var year: number = dateObject.getUTCFullYear();
  dateString = date + "/" + month + " " + year;

  var hour: string | number = dateObject.getHours();
  var min: string | number = dateObject.getMinutes();
  var timestampAmPm: string = hour >= 12 ? "PM" : "AM";
  hour = hour % 12;
  hour = hour ? hour : 12;
  min = min >= 10 ? min : "0" + min;
  timeString = hour + ":" + min + " " + timestampAmPm;

  return (
    <div>
      <div className="TimeDate">{dateString}</div>
      <div className="TimeDay">{timeString}</div>
    </div>
  );
};

const getTitleIcon = (severityString: string) => {
  const icon = {
    normal: "SuccessCircleIcon",
    warning: "WarningCircleIcon",
    error: "DangerCircleIcon",
    unknown: "UnknownCircleIcon",
  };
  if (!icon[severityString.toLowerCase()])
    return <AppUseIcon name={icon["unknown"]} width="16" height="16" />;

  return (
    <AppUseIcon
      name={icon[severityString.toLowerCase()]}
      width="16"
      height="16"
    />
  );
};

export default StatusPage;
